import { ChampionnatFirebase } from './championnat.types';
import { useGetCollection } from 'api/firestore/getCollection';
import { getDocById, useGetDocById } from 'api/firestore/getDocById';
import { createDoc } from 'api/firestore/createDoc';
import { updateDoc } from 'api/firestore/updateDoc';
import { deleteDoc } from 'api/firestore/deleteDoc';
import { CHAMPIONNATS_COLLECTION_NAME } from 'api/collectionNames.constants';
import { useMemo } from 'react';
import { useGetTournoiList } from 'features/tournois/tournoi.api';
import { mapChampionnatFirebaseToChampionnat } from './championnat.mappers';
import { Tournoi } from 'features/tournois/tournoi.types';
import { useGetTournoiImportedList } from 'features/tournois/imported/tournoi.imported.api';

export async function createChampionnat(
  championnat: Omit<ChampionnatFirebase, 'id'>,
) {
  return (
    await createDoc<ChampionnatFirebase>(
      CHAMPIONNATS_COLLECTION_NAME,
      championnat,
    )
  ).id;
}

export function createChampionnatPersonnalise(name: string, playerId: string) {
  return createChampionnat({
    name,
    isActive: true,
    type: 'personnalise',
    createdByPlayerId: playerId,
  });
}

export function updateChampionnat(championnat: ChampionnatFirebase) {
  return updateDoc<ChampionnatFirebase>(
    CHAMPIONNATS_COLLECTION_NAME,
    championnat,
  );
}
export async function closeOrOpenChampionnat(championnatId: string) {
  const championnat = await getDocById<ChampionnatFirebase>(
    CHAMPIONNATS_COLLECTION_NAME,
    championnatId,
  );
  if (championnat) {
    if (championnat.type === 'personnalise') {
      throw new Error('Utilisez les actions du championnat personnalisé.');
    }
    return updateDoc<ChampionnatFirebase>(CHAMPIONNATS_COLLECTION_NAME, {
      ...championnat,
      isActive: !championnat.isActive,
    });
  }
  return undefined;
}

export async function updateChampionnatPersonnalise(
  championnat: ChampionnatFirebase,
  playerId: string,
) {
  if (
    championnat.type !== 'personnalise' ||
    championnat.createdByPlayerId !== playerId
  ) {
    throw new Error(
      'Vous ne pouvez modifier que vos championnats personnalisés.',
    );
  }

  return updateDoc<ChampionnatFirebase>(CHAMPIONNATS_COLLECTION_NAME, {
    id: championnat.id,
    name: championnat.name,
    isActive: championnat.isActive,
    type: 'personnalise',
    createdByPlayerId: playerId,
  });
}

export async function deleteChampionnatPersonnalise(
  championnat: ChampionnatFirebase,
  playerId: string,
) {
  if (
    championnat.type !== 'personnalise' ||
    championnat.createdByPlayerId !== playerId
  ) {
    throw new Error(
      'Vous ne pouvez supprimer que vos championnats personnalisés.',
    );
  }

  return deleteDoc(CHAMPIONNATS_COLLECTION_NAME, championnat.id);
}

export function useGetChampionnatList() {
  const { data: championnatsFirebase, isLoading } =
    useGetCollection<ChampionnatFirebase>(CHAMPIONNATS_COLLECTION_NAME);
  const { tournois, isLoading: isTournoisLoading } = useGetTournoiList();
  const { tournoiImportedList, isLoading: isTournoiImportedLoading } =
    useGetTournoiImportedList();

  const championnats = useMemo(
    () =>
      !isLoading && !isTournoisLoading && !isTournoiImportedLoading
        ? championnatsFirebase
            .filter(({ type }) => type !== 'personnalise')
            .map((championnat) =>
              mapChampionnatFirebaseToChampionnat(
                championnat,
                getTournoiListForChampionnatId(
                  [...tournois, ...tournoiImportedList],
                  championnat.id,
                ),
              ),
            )
        : [],
    [
      isLoading,
      isTournoisLoading,
      isTournoiImportedLoading,
      championnatsFirebase,
      tournois,
      tournoiImportedList,
    ],
  );

  return {
    championnats,
    tournois,
    tournoisWithoutChampionnat: getTournoiListForChampionnatId(
      [...tournois, ...tournoiImportedList],
      null,
    ),
    isLoading,
  };
}

export function useGetChampionnatsPersonnalises(playerId?: string) {
  const { data: championnatsFirebase, isLoading } =
    useGetCollection<ChampionnatFirebase>(CHAMPIONNATS_COLLECTION_NAME);

  const championnats = useMemo(
    () =>
      championnatsFirebase
        .filter(
          (championnat) =>
            championnat.type === 'personnalise' &&
            championnat.createdByPlayerId === playerId,
        )
        .map((championnat) =>
          mapChampionnatFirebaseToChampionnat(championnat, []),
        ),
    [championnatsFirebase, playerId],
  );

  return { championnats, isLoading };
}

export function useGetChampionnatById(championnatId?: string) {
  const { data: championnatFirebase, isLoading } =
    useGetDocById<ChampionnatFirebase>(
      CHAMPIONNATS_COLLECTION_NAME,
      championnatId,
    );

  const { tournois, isLoading: isTournoisLoading } = useGetTournoiList();
  const { tournoiImportedList, isLoading: isTournoiImportedLoading } =
    useGetTournoiImportedList();

  const championnat = useMemo(
    () =>
      championnatFirebase && !isTournoisLoading && !isTournoiImportedLoading
        ? mapChampionnatFirebaseToChampionnat(
            championnatFirebase,
            championnatFirebase.type === 'personnalise'
              ? []
              : getTournoiListForChampionnatId(
                  [...tournois, ...tournoiImportedList],
                  championnatFirebase.id,
                ),
          )
        : undefined,
    [
      championnatFirebase,
      isTournoisLoading,
      isTournoiImportedLoading,
      tournois,
      tournoiImportedList,
    ],
  );
  return { championnat, isLoading: isLoading || isTournoisLoading };
}

function getTournoiListForChampionnatId(
  tournois: Tournoi[],
  championnatId: string | null,
) {
  return (tournois || []).filter(({ championnatIds }) =>
    championnatId
      ? championnatIds.includes(championnatId)
      : championnatIds.length === 0,
  );
}
